import { useCallback } from "react"
import {
  Avatar,
  Box,
  Button,
  Typography,
  useTheme,
  CardMedia,
} from "@mui/material"
import { useNavigate } from "react-router-dom"

import { useTranslation } from "providers/i18n"
import { useSponsoredScreensaver } from "hooks/sponsored-content/useSponsoredScreensaver"
import { useHome } from "../../hooks/home/useHome"
import { useHomeMap } from "hooks/home/useHomeMap"

import { APP_STATUS, updateAppStatus } from "utils/electron"
import { getFeatureName } from "utils/feature"

import { FullScreenOverlayLayout } from "layouts/FullScreenOverlayLayout"

import { Head } from "components/Head"
import { Carousel, CarouselItem } from "components/Carousel"
import { gtmPushDataLayerSelectLanguage } from "utils/googletagmanager"

export const HomePage = () => {
  const theme = useTheme()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { venues, locales, defaultRoutePath } = useHome()

  useHomeMap(venues)

  const { screensavers, screensaverConfig } = useSponsoredScreensaver()
  const styleOverrides = theme.components[`HomePage`]?.styleOverrides || {}
  const {
    root,
    contentContainer,
    fullscreenLayout,
    carouselWrapper,
    welcomeContainer,
    greetingText,
    localeListContainer,
    localeButton,
    actionContainer,
    actionButton,
    carouselScreenSaver,
    carouselMediaItem,
  } = styleOverrides

  const switchLanguage = useCallback(
    (lang) => (e) => {
      i18n.changeLanguage(lang)
      gtmPushDataLayerSelectLanguage(lang)
    },
    [i18n]
  )

  const handleClick = useCallback(() => {
    updateAppStatus(APP_STATUS.ACTIVE)
    navigate(defaultRoutePath)
  }, [navigate, defaultRoutePath])

  return (
    <>
      <Head title={t("Indoor map")} />
      <Box sx={root} className="page">
        <FullScreenOverlayLayout sx={fullscreenLayout}>
          <Box sx={contentContainer} className="contentContainer">
            <Box sx={carouselWrapper}>
              {screensavers.length > 0 && (
                <Carousel
                  options={{
                    type: "loop",
                    padding: 0,
                    gap: 0,
                    pagination: false,
                    drag: true,
                    ...screensaverConfig, // { autoplay: boolean , interval: number }
                  }}
                  sx={carouselScreenSaver}
                >
                  {screensavers.map((screenSaver) => (
                    <CarouselItem link={screenSaver.url} key={screenSaver.id}>
                      <CardMedia
                        sx={carouselMediaItem}
                        {...screenSaver?.props}
                      />
                    </CarouselItem>
                  ))}
                </Carousel>
              )}
            </Box>
            <Box sx={welcomeContainer}>
              <Typography variant="h3" sx={greetingText}>
                {t("Welcome to", { name: t(getFeatureName(venues[0])) })}
              </Typography>
              <Box sx={localeListContainer}>
                {locales.map((locale) => (
                  <Button
                    key={locale.localeKey}
                    onClick={switchLanguage(locale.localeKey)}
                    sx={localeButton}
                  >
                    <Avatar
                      src={`https://flagcdn.com/h40/${locale.flagCode}.webp`}
                    />
                  </Button>
                ))}
              </Box>
              <Box sx={actionContainer}>
                <Button
                  sx={actionButton}
                  variant="contained"
                  onClick={handleClick}
                >
                  {t("Touch to start")}
                </Button>
              </Box>
            </Box>
          </Box>
        </FullScreenOverlayLayout>
      </Box>
    </>
  )
}
